/**
 * Release 33 (owner, 2026-11-02): SATU installed on every device. Pasang SATU (Akun, and the
 * shell's band) words itself after the device's install situation, decided here from what the
 * browser says. Pure: the facts come in (./tawaran reads them from the page), so the node tests
 * run every device.
 *
 * - `terpasang`: opened from its icon (the display modes an installed app runs in, or iOS's
 *   `navigator.standalone`), or installed during this visit (`appinstalled`). Nothing to offer.
 * - `prompt`: the browser fired `beforeinstallprompt` and SATU holds it (Chrome and Edge on
 *   Android, Windows, macOS and ChromeOS): Pasang opens the browser's own dialog.
 * - `ios`: iPhone or iPad, in Safari or another iOS browser (all are WebKit and install through
 *   Share). iPadOS 13+ asks for the desktop site and says Macintosh: its touch points tell.
 * - `mac-safari`: Safari on a Mac (Share › Tambahkan ke Dock, Safari 17+).
 * - `belum-tersedia`: a Chromium browser that has not offered its prompt (yet: its engagement
 *   rules, or the person dismissed it before): its own menu still installs.
 * - `tidak-bisa`: anything else (Firefox on a computer, among others): no install path here.
 */
export type SituasiPasang =
  | 'terpasang'
  | 'prompt'
  | 'ios'
  | 'mac-safari'
  | 'belum-tersedia'
  | 'tidak-bisa'

export interface FaktaPeramban {
  userAgent: string
  maxTouchPoints: number
  /** iOS Safari's `navigator.standalone`: true in a Home Screen app; undefined elsewhere. */
  standalone: boolean | undefined
  /** `matchMedia(query).matches` */
  tampilan(query: string): boolean
  /** A `beforeinstallprompt` event is held. */
  adaPrompt: boolean
  /** `appinstalled` fired during this visit. */
  baruTerpasang?: boolean
}

/** The display modes an installed app opens in (the manifest asks for standalone). */
export const MODE_TERPASANG = [
  '(display-mode: standalone)',
  '(display-mode: window-controls-overlay)',
  '(display-mode: minimal-ui)',
] as const

export function diAppleSeluler(f: Pick<FaktaPeramban, 'userAgent' | 'maxTouchPoints'>): boolean {
  if (/\b(iPhone|iPad|iPod)\b/.test(f.userAgent)) return true
  return /\bMacintosh\b/.test(f.userAgent) && f.maxTouchPoints > 1
}

// Chrome without a window (automated checks) names itself HeadlessChrome.
const BUKAN_SAFARI =
  /\b(HeadlessChrome|Chrome|Chromium|CriOS|Edg|EdgA|EdgiOS|Firefox|FxiOS|OPR|SamsungBrowser)\//
const CHROMIUM = /\b(HeadlessChrome|Chrome|Chromium|Edg|EdgA|SamsungBrowser)\//

export function situasiPasang(f: FaktaPeramban): SituasiPasang {
  if (f.baruTerpasang === true || f.standalone === true) return 'terpasang'
  if (MODE_TERPASANG.some((q) => f.tampilan(q))) return 'terpasang'
  if (f.adaPrompt) return 'prompt'
  if (diAppleSeluler(f)) return 'ios'
  const ua = f.userAgent
  if (/\bMacintosh\b/.test(ua) && /\bSafari\//.test(ua) && !BUKAN_SAFARI.test(ua)) {
    return 'mac-safari'
  }
  if (CHROMIUM.test(ua) && !/\bFirefox\//.test(ua)) return 'belum-tersedia'
  return 'tidak-bisa'
}

/** Which iOS browser: the Share button sits in Safari's bar or menu, Chrome's and Edge's own. */
export function perambanIos(userAgent: string): 'safari' | 'chrome' | 'edge' | 'lain' {
  if (/\bCriOS\//.test(userAgent)) return 'chrome'
  if (/\bEdgiOS\//.test(userAgent)) return 'edge'
  if (/\b(FxiOS|OPiOS|OPT)\//.test(userAgent)) return 'lain'
  return 'safari'
}
